import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { ChevronDown, Users, Volume2, X, Play } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getAssetUrl } from '../../utils/assetUrl';
import StarRating from './StarRating';
import type { PublicTestimonial } from './types';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** Prévia muda em loop que só toca enquanto visível na tela. */
const LoopPreview: React.FC<{ src: string; poster: string; label: string; onOpen: () => void }> = ({ src, poster, label, onOpen }) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLVideoElement>(null);
  const [autoplay] = useState(() => !prefersReducedMotion());

  useEffect(() => {
    const el = ref.current;
    if (!el || !autoplay || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) el.play().catch(() => {});
      else el.pause();
    }, { threshold: 0.4 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [autoplay]);

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`${label} — ${t('testimonialsUi.playWithSound')}`}
      className="group relative -mx-6 -mt-6 mb-5 block aspect-[4/5] w-[calc(100%+3rem)] overflow-hidden rounded-t-xl bg-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
    >
      <video
        ref={ref}
        src={src}
        poster={poster || undefined}
        muted
        loop
        playsInline
        preload={autoplay ? 'metadata' : 'none'}
        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
      />
      <span className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/60 to-transparent" />
      <span className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1.5 text-xs font-semibold text-gray-900 shadow">
        {autoplay ? <Volume2 className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5 fill-current" />}
        {t('testimonialsUi.playWithSound')}
      </span>
    </button>
  );
};

const VideoModal: React.FC<{ src: string; poster: string; label: string; onClose: () => void }> = ({ src, poster, label, onClose }) => {
  const { t } = useTranslation();
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={label}
      onClick={onClose}
    >
      <div className="relative w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          aria-label={t('testimonialsUi.closeVideo')}
          className="absolute -top-12 right-0 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
        >
          <X className="h-6 w-6" />
        </button>
        <video src={src} poster={poster || undefined} controls autoPlay playsInline className="max-h-[80vh] w-full rounded-xl bg-black object-contain" />
      </div>
    </div>
  );
};

/** Texto do depoimento cortado em algumas linhas, com "Leia mais" só quando passa do limite. */
const ExpandableText: React.FC<{ text: string; cardRef: React.RefObject<HTMLDivElement | null> }> = ({ text, cardRef }) => {
  const { t } = useTranslation();
  const id = useId();
  const ref = useRef<HTMLParagraphElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  // O clamp só volta quando a animação de recolher termina, senão o texto cortaria antes da altura descer.
  const [clamped, setClamped] = useState(true);
  const [overflowing, setOverflowing] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !clamped) return;
    const measure = () => setOverflowing(el.scrollHeight > el.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text, clamped]);

  const toggle = () => {
    const box = boxRef.current;
    const el = ref.current;
    if (expanded) {
      const card = cardRef.current;
      if (card && card.getBoundingClientRect().top < 0) {
        card.scrollIntoView({ block: 'nearest', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      }
    }
    if (!box || !el || prefersReducedMotion()) {
      setClamped(expanded);
      setExpanded(!expanded);
      return;
    }
    // Anima a altura em px a partir da atual (inclusive no meio de uma transição) até o alvo.
    const target = expanded ? parseFloat(getComputedStyle(el).lineHeight) * 5 : el.scrollHeight;
    box.style.height = `${box.getBoundingClientRect().height}px`;
    void box.offsetHeight;
    box.style.height = `${target}px`;
    setClamped(false);
    setExpanded(!expanded);
  };

  const onTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget || e.propertyName !== 'height') return;
    e.currentTarget.style.height = '';
    if (!expanded) setClamped(true);
  };

  return (
    <div className="mb-6 flex-1">
      <div
        ref={boxRef}
        onTransitionEnd={onTransitionEnd}
        className="overflow-hidden transition-[height] duration-300 ease-out motion-reduce:transition-none"
      >
        <p id={id} ref={ref} className={`italic text-gray-600 ${clamped ? 'line-clamp-5' : ''}`}>&quot;{text}&quot;</p>
      </div>
      {(overflowing || expanded) && (
        <button
          type="button"
          onClick={toggle}
          aria-expanded={expanded}
          aria-controls={id}
          className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-800"
        >
          {expanded ? t('testimonialsUi.readLess') : t('testimonialsUi.readMore')}
          <ChevronDown className={`h-4 w-4 transition-transform duration-300 motion-reduce:transition-none ${expanded ? 'rotate-180' : ''}`} />
        </button>
      )}
    </div>
  );
};

const TestimonialCard: React.FC<{ item: PublicTestimonial }> = ({ item }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  const videoUrl = item.video?.url ? getAssetUrl(item.video.url) : '';
  const posterUrl = item.video?.posterUrl ? getAssetUrl(item.video.posterUrl) : '';
  const label = t('testimonialsUi.videoBy', { name: item.author });

  return (
    <div ref={cardRef} className="card flex flex-col overflow-hidden">
      {videoUrl && <LoopPreview src={videoUrl} poster={posterUrl} label={label} onOpen={() => setOpen(true)} />}
      <div className="mb-4">
        <StarRating value={item.rating || 5} />
      </div>
      {item.text && <ExpandableText text={item.text} cardRef={cardRef} />}
      <div className="mt-auto flex items-center space-x-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100">
          <Users className="h-5 w-5 text-primary-600" />
        </div>
        <div>
          <div className="font-semibold text-gray-900">{item.author}</div>
          {item.country && <div className="text-sm text-gray-500">{item.country}</div>}
        </div>
      </div>
      {open && videoUrl && <VideoModal src={videoUrl} poster={posterUrl} label={label} onClose={close} />}
    </div>
  );
};

export default TestimonialCard;
